import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminSession } from '../utils/session';
import { revokeStaffToken } from '../services/authApi';
import { fetchMe } from '../services/userApi';
import { store } from '../store';
import { setUser, clearUser } from '../features/auth/authSlice';
import { normalizeStaffUser } from '../utils/staffUser';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const me = await fetchMe();
        if (cancelled) {
          return;
        }

        const userData = normalizeStaffUser(me);
        const expiresAt = me.session?.expires_at;
        adminSession.setSession(userData, { expiresAt });
        store.dispatch(
          setUser({
            user: userData,
            token: adminSession.getBearerToken(),
          })
        );
        setIsAuthenticated(true);
      } catch {
        if (cancelled) {
          return;
        }
        adminSession.clear();
        store.dispatch(clearUser());
        setIsAuthenticated(false);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = ({ expiresIn, expiresAt } = {}) => {
    if (expiresIn || expiresAt) {
      adminSession.setSession(null, { expiresIn, expiresAt });
    }
    setIsAuthenticated(true);
  };

  const logout = async () => {
    await revokeStaffToken().finally(() => {
      adminSession.clear();
      store.dispatch(clearUser());
      setIsAuthenticated(false);
    });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
